import { useState } from 'react';
import { withApollo } from 'react-apollo';
import { Formik } from 'formik';
import Button from '../Button';
import Form from '../Form';
import Input from '../Input';
import { RESET_USER_PASSWORD, SEND_USER_PASSWORD_RESET_LINK } from '../../../admin/mutations';

const formItemLayout = {
  labelCol: {
    xs: { span: 24 },
    sm: { span: 8 },
  },
  wrapperCol: {
    xs: { span: 24 },
    sm: { span: 16 },
  },
};

const ResetUserPasswordForm = ({ onComplete, client, userId, userEmail }) => {
  const [passwordError, setPasswordError] = useState('');
  const [hasError, setHasError] = useState(false);
  const [linkSending, setLinkSending] = useState(false);
  const [linkMessage, setLinkMessage] = useState('');

  const handleSendResetLink = async () => {
    setLinkSending(true);
    setLinkMessage('');
    try {
      await client.mutate({
        mutation: SEND_USER_PASSWORD_RESET_LINK,
        variables: { id: userId, email: userEmail },
      });
      setLinkMessage(`Reset link emailed to ${userEmail}.`);
    } catch (err) {
      const reason = err?.graphQLErrors?.[0]?.message || err?.message || '';
      setLinkMessage(reason.replace(/^GraphQL error:\s*/, '') || 'Could not send reset link.');
    } finally {
      setLinkSending(false);
    }
  };

  return (
    <Formik
      initialValues={{
        password: '',
        email: userEmail,
      }}
      validate={values => {
        const errors = {};
        if (!values.password) {
          errors.password = 'A new password is required.';
        }
        return errors;
      }}
      onSubmit={async (values, { setSubmitting }) => {
        const { email, password } = values;
        return client
          .mutate({
            mutation: RESET_USER_PASSWORD,
            variables: {
              id: userId,
              email,
              password,
            },
          })
          .then(() => {
            setSubmitting(false);
            onComplete();
            values.password = '';
          })
          .catch(err => {
            console.log('Error Message: ', err.message);
            const reason = err?.graphQLErrors?.[0]?.message || '';
            setHasError(true);
            setPasswordError(reason.replace(/^GraphQL error:\s*/, '') || 'Oops something went wrong');
          });
      }}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
        <>
          <Form onSubmit={handleSubmit} {...formItemLayout}>
            <Form.Item label="Email" hasFeedback required>
              <Input
                type="email"
                name="email"
                disabled
                placeholder={userEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                value={userEmail}
              />
              <div className="leading-4">Sets the password directly. The user is not emailed.</div>
            </Form.Item>
            <Form.Item
              label="Password"
              hasFeedback
              validateStatus={errors.password && touched.password && 'error'}
              help={errors.password && touched.password && errors.password}
            >
              <Input
                type="password"
                name="password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
              />
              {hasError && <p>{passwordError}</p>}
            </Form.Item>
            <Form.Item
              wrapperCol={{
                xs: { span: 24, offset: 0 },
                sm: { span: 16, offset: 8 },
              }}
            />
            <Button loading={isSubmitting} type="primary" htmlType="submit">
              Reset User Password
            </Button>
          </Form>
          <div className="mt-6 border-t pt-4">
            <p className="mb-2 font-semibold">Send password reset link</p>
            <p className="leading-4 mb-3">
              Emails the user a secure link to set their own password (valid 24 hours). No password
              is set here.
            </p>
            <Button loading={linkSending} onClick={handleSendResetLink}>
              Send Reset Link
            </Button>
            {linkMessage && <p className="mt-2">{linkMessage}</p>}
          </div>
        </>
      )}
    </Formik>
  );
};

export default withApollo(ResetUserPasswordForm);
